import type { AxiosError } from "axios";
import type { FailedRequestCapture } from "./types";
import { normalizeApiPath } from "./fix-hints";
import { sanitizeCaptureValue } from "./sanitize";

const MAX_ENTRIES = 10;
const FAILED_REQUESTS_KEY = "debug-capture:failed-requests";
const FAILED_REQUESTS_TTL_MS = 24 * 60 * 60 * 1000;

const buffer: FailedRequestCapture[] = [];
let hydrated = false;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function persistFailedRequestsBuffer(): void {
  if (!isBrowser()) {
    return;
  }

  try {
    sessionStorage.setItem(
      FAILED_REQUESTS_KEY,
      JSON.stringify({
        updatedAt: new Date().toISOString(),
        entries: buffer,
      })
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

export function hydrateFailedRequestsBuffer(): void {
  if (!isBrowser() || hydrated) {
    return;
  }

  hydrated = true;

  try {
    const raw = sessionStorage.getItem(FAILED_REQUESTS_KEY);
    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw) as {
      updatedAt?: string;
      entries?: FailedRequestCapture[];
    };

    if (parsed.updatedAt) {
      const age = Date.now() - new Date(parsed.updatedAt).getTime();
      if (age > FAILED_REQUESTS_TTL_MS) {
        sessionStorage.removeItem(FAILED_REQUESTS_KEY);
        return;
      }
    }

    if (Array.isArray(parsed.entries)) {
      buffer.length = 0;
      buffer.push(...parsed.entries.slice(0, MAX_ENTRIES));
    }
  } catch {
    sessionStorage.removeItem(FAILED_REQUESTS_KEY);
  }
}

function buildRequestUrl(baseURL: string | undefined, url: string | undefined): string {
  if (!url) {
    return baseURL ?? "unknown";
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const base = (baseURL ?? "").replace(/\/$/, "");
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${base}${path}`;
}

export function recordFailedRequest(entry: Omit<FailedRequestCapture, "timestamp">): void {
  const normalized = normalizeApiPath(entry.path ?? entry.url);

  buffer.unshift({
    timestamp: new Date().toISOString(),
    ...entry,
    path: entry.path ?? normalized.path,
    query: entry.query ?? normalized.query,
  });

  if (buffer.length > MAX_ENTRIES) {
    buffer.length = MAX_ENTRIES;
  }

  persistFailedRequestsBuffer();
}

export function getFailedRequests(): FailedRequestCapture[] {
  return buffer.map((entry) => ({ ...entry }));
}

export function clearFailedRequests(): void {
  buffer.length = 0;
  if (isBrowser()) {
    sessionStorage.removeItem(FAILED_REQUESTS_KEY);
  }
}

export function recordAxiosFailure(error: AxiosError): void {
  const payload = error.config?.data ?? error.config?.params;
  const fullUrl = buildRequestUrl(error.config?.baseURL, error.config?.url);
  const normalized = normalizeApiPath(fullUrl);
  const startTime = error.config?.metadata?.startTime;

  recordFailedRequest({
    method: (error.config?.method ?? "GET").toUpperCase(),
    url: fullUrl,
    path: normalized.path,
    query: normalized.query,
    durationMs:
      startTime != null ? Math.max(0, Date.now() - startTime) : undefined,
    status: error.response?.status,
    statusText: error.response?.statusText,
    requestPayload: sanitizeCaptureValue(payload),
    responseBody: sanitizeCaptureValue(error.response?.data),
    message: error.message,
  });
}
